import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { AcsButton, AcsCard, AcsCheckbox, AcsInput, AcsLogo, AcsPageShell } from '../components';
import { useI18n } from '../i18n/I18nProvider';
import { api } from '../api/client';

/**
 * Creazione di una fiera.
 *
 * La fiera nasce sempre in `DRAFT`: l'attivazione è un atto separato, che
 * richiede un'informativa pubblicata (§19). La schermata lo dice, invece di
 * lasciare che l'utente lo scopra al primo tentativo.
 */

const LANGUAGE_CHOICES = ['it', 'en'] as const;

export function NewEventPage() {
  const { t } = useI18n();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [location, setLocation] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [languages, setLanguages] = useState<string[]>(['it']);
  const [headline, setHeadline] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.createEvent({
        name: name.trim(),
        slug: slug.trim(),
        ...(location.trim() ? { location: location.trim() } : {}),
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        languages,
        ...(headline.trim() || shortDescription.trim()
          ? {
              branding: {
                ...(headline.trim() ? { headline: headline.trim() } : {}),
                ...(shortDescription.trim()
                  ? { shortDescription: shortDescription.trim() }
                  : {}),
              },
            }
          : {}),
      }),
    onSuccess: (event) => navigate(`/app/events/${event.id}`),
    onError: (cause: Error) => setServerError(cause.message),
  });

  /** Suggerisce lo slug dal nome, senza impedire di modificarlo. */
  function handleNameChange(value: string): void {
    setName(value);
    if (slug === '' || slug === toSlug(name)) setSlug(toSlug(value));
  }

  function validate(): Record<string, string> {
    const found: Record<string, string> = {};
    if (name.trim().length === 0) found.name = t.kiosk.errorRequired;
    if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug.trim())) found.slug = t.newEvent.errorSlug;
    if (startsAt === '') found.startsAt = t.kiosk.errorRequired;
    if (endsAt === '') found.endsAt = t.kiosk.errorRequired;
    if (startsAt && endsAt && new Date(endsAt) < new Date(startsAt)) {
      found.endsAt = t.newEvent.errorDates;
    }
    if (languages.length === 0) found.languages = t.newEvent.errorLanguages;
    return found;
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    setServerError(null);
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length === 0) create.mutate();
  }

  return (
    <AcsPageShell header={<AcsLogo />}>
      <Link to="/app" style={{ color: 'var(--acs-color-primary)' }}>
        ← {t.app.back}
      </Link>
      <h1>{t.newEvent.title}</h1>

      <AcsCard>
        <p style={{ color: 'var(--acs-color-text-muted)', marginTop: 0 }}>
          {t.newEvent.draftNotice}
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <AcsInput
            label={t.newEvent.name}
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            autoComplete="off"
            error={errors.name}
            required
          />
          <AcsInput
            label={t.newEvent.slug}
            optionalHint={t.newEvent.slugHint}
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            autoComplete="off"
            error={errors.slug}
            required
          />
          <AcsInput
            label={t.newEvent.location}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            autoComplete="off"
          />
          <AcsInput
            label={t.newEvent.startsAt}
            type="date"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            error={errors.startsAt}
            required
          />
          <AcsInput
            label={t.newEvent.endsAt}
            type="date"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
            error={errors.endsAt}
            required
          />

          <fieldset style={{ border: 'none', padding: 0, marginBottom: 'var(--acs-space-4)' }}>
            <legend className="acs-field__label">{t.newEvent.languages}</legend>
            {LANGUAGE_CHOICES.map((code) => (
              <AcsCheckbox
                key={code}
                label={code.toUpperCase()}
                checked={languages.includes(code)}
                onChange={() =>
                  setLanguages((current) =>
                    current.includes(code)
                      ? current.filter((c) => c !== code)
                      : [...current, code],
                  )
                }
              />
            ))}
            {errors.languages ? (
              <span className="acs-field__error" role="alert">
                {errors.languages}
              </span>
            ) : null}
          </fieldset>

          <AcsInput
            label={t.newEvent.headline}
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            autoComplete="off"
          />
          <AcsInput
            label={t.newEvent.shortDescription}
            value={shortDescription}
            onChange={(e) => setShortDescription(e.target.value)}
            autoComplete="off"
          />

          {serverError ? (
            <p className="acs-field__error" role="alert">
              {serverError}
            </p>
          ) : null}

          <AcsButton type="submit" disabled={create.isPending}>
            {t.newEvent.create}
          </AcsButton>
        </form>
      </AcsCard>
    </AcsPageShell>
  );
}

function toSlug(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
